import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import './Onboarding.css';
import logo from '../../assets/Logo - Side.png';

function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [companyId, setCompanyId] = useState(null);
  
  const [companyData, setCompanyData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    website: ''
  });

  const [brandingData, setBrandingData] = useState({
    logo_url: '',
    primary_color: '#1457f5',
    secondary_color: '#60aaff',
    tagline: ''
  });

  const [propertyData, setPropertyData] = useState({
    property_name: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    property_type: 'single_family',
    num_units: 1
  });

  useEffect(() => {
    // Get company ID from session
    const saasUser = localStorage.getItem('saasUser');
    if (saasUser) {
      const userData = JSON.parse(saasUser);
      setCompanyId(userData.company_id);
      if (userData.company_name) {
        setCompanyData(prev => ({ ...prev, name: userData.company_name }));
      }
    }
  }, []);

  const handleCompanyChange = (e) => {
    setCompanyData({ ...companyData, [e.target.name]: e.target.value });
  };

  const handleBrandingChange = (e) => {
    setBrandingData({ ...brandingData, [e.target.name]: e.target.value });
  };

  const handlePropertyChange = (e) => {
    setPropertyData({ ...propertyData, [e.target.name]: e.target.value });
  };

  const saveStep = async (stepName, data) => {
    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_BASE_URL}/api/saas/complete-onboarding`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_id: companyId,
          step: stepName,
          ...data
        })
      });

      const result = await res.json();

      if (!res.ok) {
        setError(result.error || 'Failed to save');
        return false;
      }
      return true;
    } catch (error) {
      console.error('Save error:', error);
      setError('An error occurred. Please try again.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleNextStep = async () => {
    let success = false;
    
    if (step === 1) {
      success = await saveStep('company_profile', companyData);
    } else if (step === 2) {
      success = await saveStep('branding', brandingData);
    } else if (step === 3) {
      success = await saveStep('first_property', propertyData);
    }

    if (success) {
      if (step < 4) {
        setStep(step + 1);
      } else {
        navigate('/dashboard');
      }
    }
  };

  const handleSkip = () => {
    if (step < 4) {
      setStep(step + 1);
    } else {
      navigate('/dashboard');
    }
  };

  const completeOnboarding = () => {
    navigate('/dashboard');
  };

  return (
    <div className="onboarding-page">
      <div className="onboarding-sidebar">
        <img src={logo} alt="RentafAI" className="onboarding-logo" />
        
        <div className="onboarding-steps">
          <div className={`onboarding-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
            <div className="step-indicator">
              {step > 1 ? '✓' : '1'}
            </div>
            <div className="step-info">
              <h4>Company Profile</h4>
              <p>Basic information</p>
            </div>
          </div>
          
          <div className={`onboarding-step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
            <div className="step-indicator">
              {step > 2 ? '✓' : '2'}
            </div>
            <div className="step-info">
              <h4>Branding</h4>
              <p>Logo & colors</p>
            </div>
          </div>
          
          <div className={`onboarding-step ${step >= 3 ? 'active' : ''} ${step > 3 ? 'completed' : ''}`}>
            <div className="step-indicator">
              {step > 3 ? '✓' : '3'}
            </div>
            <div className="step-info">
              <h4>First Property</h4>
              <p>Add a property</p>
            </div>
          </div>
          
          <div className={`onboarding-step ${step >= 4 ? 'active' : ''}`}>
            <div className="step-indicator">4</div>
            <div className="step-info">
              <h4>All Done!</h4>
              <p>Ready to go</p>
            </div>
          </div>
        </div>

        <div className="onboarding-help">
          <p>Need help? <a href="mailto:support@rentafai.com">Contact Support</a></p>
        </div>
      </div>

      <div className="onboarding-content">
        {error && <div className="onboarding-error">{error}</div>}

        {/* Step 1: Company Profile */}
        {step === 1 && (
          <div className="onboarding-form">
            <div className="form-header">
              <h2>Company Profile</h2>
              <p>Tell us about your property management company</p>
            </div>

            <div className="form-body">
              <div className="form-group">
                <label>Company Name *</label>
                <input
                  type="text"
                  name="name"
                  value={companyData.name}
                  onChange={handleCompanyChange}
                  placeholder="Your Property Management Company"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={companyData.phone}
                    onChange={handleCompanyChange}
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={companyData.email}
                    onChange={handleCompanyChange}
                    placeholder="contact@company.com"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Street Address</label>
                <input
                  type="text"
                  name="address"
                  value={companyData.address}
                  onChange={handleCompanyChange}
                  placeholder="123 Main Street"
                />
              </div>

              <div className="form-row form-row-3">
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    name="city"
                    value={companyData.city}
                    onChange={handleCompanyChange}
                    placeholder="City"
                  />
                </div>
                <div className="form-group">
                  <label>State</label>
                  <input
                    type="text"
                    name="state"
                    value={companyData.state}
                    onChange={handleCompanyChange}
                    placeholder="State"
                  />
                </div>
                <div className="form-group">
                  <label>ZIP Code</label>
                  <input
                    type="text"
                    name="zip_code"
                    value={companyData.zip_code}
                    onChange={handleCompanyChange}
                    placeholder="12345"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Website</label>
                <input
                  type="url"
                  name="website"
                  value={companyData.website}
                  onChange={handleCompanyChange}
                  placeholder="https://www.company.com"
                />
              </div>
            </div>

            <div className="form-actions">
              <button className="btn btn-ghost" onClick={handleSkip}>
                Skip for now
              </button>
              <button className="btn btn-primary" onClick={handleNextStep} disabled={loading}>
                {loading ? 'Saving...' : 'Continue'}
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Branding */}
        {step === 2 && (
          <div className="onboarding-form">
            <div className="form-header">
              <h2>Branding</h2>
              <p>Customize how your company appears to tenants</p>
            </div>

            <div className="form-body">
              <div className="form-group">
                <label>Logo URL</label>
                <input
                  type="url"
                  name="logo_url"
                  value={brandingData.logo_url}
                  onChange={handleBrandingChange}
                  placeholder="https://example.com/logo.png"
                />
                <p className="form-hint">Enter a URL to your company logo (recommended: 200x60px PNG)</p>
              </div>

              {brandingData.logo_url && (
                <div className="logo-preview">
                  <label>Logo Preview</label>
                  <div className="preview-box">
                    <img src={brandingData.logo_url} alt="Logo preview" />
                  </div>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Primary Color</label>
                  <div className="color-input">
                    <input
                      type="color"
                      name="primary_color"
                      value={brandingData.primary_color}
                      onChange={handleBrandingChange}
                    />
                    <input
                      type="text"
                      value={brandingData.primary_color}
                      onChange={handleBrandingChange}
                      name="primary_color"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Secondary Color</label>
                  <div className="color-input">
                    <input
                      type="color"
                      name="secondary_color"
                      value={brandingData.secondary_color}
                      onChange={handleBrandingChange}
                    />
                    <input
                      type="text"
                      value={brandingData.secondary_color}
                      onChange={handleBrandingChange}
                      name="secondary_color"
                    />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>Tagline</label>
                <input
                  type="text"
                  name="tagline"
                  value={brandingData.tagline}
                  onChange={handleBrandingChange}
                  placeholder="Your trusted property management partner"
                />
              </div>

              <div className="brand-preview">
                <label>Email Header Preview</label>
                <div 
                  className="email-preview"
                  style={{ background: `linear-gradient(135deg, ${brandingData.primary_color} 0%, ${brandingData.secondary_color} 100%)` }}
                >
                  {brandingData.logo_url ? (
                    <img src={brandingData.logo_url} alt="Logo" className="preview-logo" />
                  ) : (
                    <div className="preview-company-name">{companyData.name || 'Your Company'}</div>
                  )}
                  {brandingData.tagline && (
                    <div className="preview-tagline">{brandingData.tagline}</div>
                  )}
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button className="btn btn-ghost" onClick={() => setStep(1)}>
                Back
              </button>
              <button className="btn btn-ghost" onClick={handleSkip}>
                Skip for now
              </button>
              <button className="btn btn-primary" onClick={handleNextStep} disabled={loading}>
                {loading ? 'Saving...' : 'Continue'}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: First Property */}
        {step === 3 && (
          <div className="onboarding-form">
            <div className="form-header">
              <h2>Add Your First Property</h2>
              <p>Enter details for a property you manage</p>
            </div>

            <div className="form-body">
              <div className="form-group">
                <label>Property Name *</label>
                <input
                  type="text"
                  name="property_name"
                  value={propertyData.property_name}
                  onChange={handlePropertyChange}
                  placeholder="Sunset Apartments"
                />
              </div>

              <div className="form-group">
                <label>Street Address *</label>
                <input
                  type="text"
                  name="address"
                  value={propertyData.address}
                  onChange={handlePropertyChange}
                  placeholder="456 Oak Street"
                />
              </div>

              <div className="form-row form-row-3">
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    name="city"
                    value={propertyData.city}
                    onChange={handlePropertyChange}
                    placeholder="City"
                  />
                </div>
                <div className="form-group">
                  <label>State</label>
                  <input
                    type="text"
                    name="state"
                    value={propertyData.state}
                    onChange={handlePropertyChange}
                    placeholder="State"
                  />
                </div>
                <div className="form-group">
                  <label>ZIP Code</label>
                  <input
                    type="text"
                    name="zip_code"
                    value={propertyData.zip_code}
                    onChange={handlePropertyChange}
                    placeholder="12345"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Property Type</label>
                  <select
                    name="property_type"
                    value={propertyData.property_type}
                    onChange={handlePropertyChange}
                  >
                    <option value="single_family">Single Family</option>
                    <option value="multi_family">Multi-Family</option>
                    <option value="apartment">Apartment Building</option>
                    <option value="condo">Condo</option>
                    <option value="townhouse">Townhouse</option>
                    <option value="commercial">Commercial</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Number of Units</label>
                  <input
                    type="number"
                    name="num_units"
                    value={propertyData.num_units}
                    onChange={handlePropertyChange}
                    min="1"
                  />
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button className="btn btn-ghost" onClick={() => setStep(2)}>
                Back
              </button>
              <button className="btn btn-ghost" onClick={handleSkip}>
                Skip for now
              </button>
              <button className="btn btn-primary" onClick={handleNextStep} disabled={loading}>
                {loading ? 'Saving...' : 'Continue'}
              </button>
            </div>
          </div>
        )}

        {/* Step 4: All Done */}
        {step === 4 && (
          <div className="onboarding-form onboarding-complete">
            <div className="complete-icon">🎉</div>
            <h2>You're All Set!</h2>
            <p>Your account is ready. Here's what you can do next:</p>

            <div className="next-steps">
              <div className="next-step-item">
                <span className="step-icon">🏠</span>
                <div>
                  <h4>Add more properties</h4>
                  <p>Import or manually add your property portfolio</p>
                </div>
              </div>
              <div className="next-step-item">
                <span className="step-icon">👥</span>
                <div>
                  <h4>Invite tenants</h4>
                  <p>Send invites to your tenants to join the portal</p>
                </div>
              </div>
              <div className="next-step-item">
                <span className="step-icon">💳</span>
                <div>
                  <h4>Set up payments</h4>
                  <p>Connect Stripe to accept online rent payments</p>
                </div>
              </div>
            </div>

            <button className="btn btn-primary btn-lg" onClick={completeOnboarding}>
              Go to Dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Onboarding;
